import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, ArrowRight, Eye, EyeOff, Sparkles, Users, Zap, Shield } from 'lucide-react';
import ThemeToggle from '../components/ThemeToggle';
import Button from '../components/ui/Button';
import SsoButtons from '../components/auth/SsoButtons';
import livePulseLogo from '../assets/live-pulse-logo.png';
import { API_PATH, request } from '../config/api';
import { useAuth } from '../store/AuthContext';
import authStyles from '../components/auth/AuthExperience.module.css';
import styles from './Login.module.css';

const Login = () => {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        try {
            const response = await request(`${API_PATH}/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password }),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Login failed');
            login(data.token, rememberMe, data.user);
            navigate('/dashboard');
        } catch (err) {
            setError(err.message || 'Something went wrong. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const TRUST_ITEMS = [
        { icon: <Users size={16} />, text: 'No app or install for participants' },
        { icon: <Zap size={16} />, text: 'Live results the moment votes come in' },
        { icon: <Shield size={16} />, text: 'Free plan available, no card required' },
    ];
    const LEFT_METRICS = [
        { label: 'Activity types', value: '27' },
        { label: 'Join code', value: '6-digit' },
        { label: 'To get started', value: '₹0' },
    ];
    const HOW_IT_WORKS = [
        { title: 'Share a 6-digit code', desc: 'Or a QR — no app to install' },
        { title: 'Audience responds live', desc: 'From any phone or laptop browser' },
        { title: 'Results appear instantly', desc: 'Charts update the moment votes land' },
    ];

    return (
        <div className={`${styles.page} ${authStyles.page}`}>
            {/* ── Left brand panel ─────────────────────────── */}
            <div className={`${styles.leftPanel} ${authStyles.leftPanel}`}>
                <div className={styles.orb1} />
                <div className={styles.orb2} />
                <div className={styles.orb3} />

                <Link to="/" className={styles.brandLink}>
                    <img src={livePulseLogo} alt="LivePulse" className={styles.brandName} />
                </Link>

                <div className={styles.leftContent}>
                    <div className={styles.leftBadge}>
                        <Sparkles size={13} />
                        <span>Real-time audience intelligence</span>
                    </div>

                    <h2 className={styles.leftTitle}>
                        Engage your
                        <br />
                        <span className={styles.leftTitleGrad}>audience live</span>
                    </h2>

                    <p className={styles.leftDesc}>
                        Live polls, Q&A, quizzes, word clouds — everything to turn passive viewers into active participants.
                    </p>

                    <div className={styles.metricStrip}>
                        {LEFT_METRICS.map((item, i) => (
                            <div key={item.label} className={styles.metricItem} style={{ '--delay': `${i * 120}ms` }}>
                                <strong>{item.value}</strong>
                                <span>{item.label}</span>
                            </div>
                        ))}
                    </div>

                    <div className={styles.trustList}>
                        {TRUST_ITEMS.map((item, i) => (
                            <div key={i} className={styles.trustItem} style={{ '--delay': `${240 + i * 120}ms` }}>
                                <div className={styles.trustIcon}>{item.icon}</div>
                                <span>{item.text}</span>
                            </div>
                        ))}
                    </div>

                    <div className={styles.mockCard}>
                        <div className={styles.mockHeader}>
                            <Zap size={14} className={styles.mockHeaderIcon} />
                            <span className={styles.mockLiveText}>How LivePulse works</span>
                        </div>
                        {HOW_IT_WORKS.map((step, i) => (
                            <div key={step.title} className={styles.stepRow} style={{ '--delay': `${580 + i * 110}ms` }}>
                                <span className={styles.stepNum}>{i + 1}</span>
                                <div className={styles.stepBody}>
                                    <strong className={styles.stepTitle}>{step.title}</strong>
                                    <span className={styles.stepDesc}>{step.desc}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className={styles.leftFooter}>© {new Date().getFullYear()} LivePulse. All rights reserved. Design & Developed by <a href="https://ottosoft.in" target="_blank" rel="noopener noreferrer">Ottosoft Private Limited</a></div>
            </div>

            {/* ── Right form panel ─────────────────────────── */}
            <div className={`${styles.rightPanel} ${authStyles.rightPanel}`}>
                <div className={styles.themeBtn}><ThemeToggle /></div>

                <div className={`${styles.formCard} ${authStyles.formCard}`}>
                    {/* Mobile-only logo */}
                    <Link to="/" className={styles.mobileLogoLink}>
                        <img src={livePulseLogo} alt="LivePulse" className={styles.mobileLogoName} />
                    </Link>

                    <div className={styles.formHeader}>
                        <h1 className={styles.formTitle}>Welcome back</h1>
                        <p className={styles.formSubtitle}>Sign in to your LivePulse account</p>
                    </div>

                    <SsoButtons
                        disabled={isLoading}
                        dividerLabel="or sign in with email"
                        onGoogle={() => {
                            const redirect = `${window.location.origin}/dashboard`;
                            window.location.href = `${API_PATH}/auth/oauth/google?redirect=${encodeURIComponent(redirect)}`;
                        }}
                        onMicrosoft={() => {
                            const redirect = `${window.location.origin}/dashboard`;
                            window.location.href = `${API_PATH}/auth/oauth/microsoft?redirect=${encodeURIComponent(redirect)}`;
                        }}
                    />

                    {error && (
                        <div className={styles.errorBanner}>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 12.5a5.5 5.5 0 110-11 5.5 5.5 0 010 11zm-.75-8.25a.75.75 0 011.5 0v3.5a.75.75 0 01-1.5 0v-3.5zm.75 6a.875.875 0 110-1.75.875.875 0 010 1.75z" />
                            </svg>
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className={styles.form}>
                        <div className={styles.formGroup}>
                            <label htmlFor="email" className={styles.label}>Email address</label>
                            <div className={styles.inputWrapper}>
                                <Mail size={17} className={styles.inputIcon} />
                                <input
                                    id="email"
                                    type="email"
                                    placeholder="name@company.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className={styles.input}
                                    required
                                    disabled={isLoading}
                                    autoComplete="email"
                                />
                            </div>
                        </div>

                        <div className={styles.formGroup}>
                            <div className={styles.labelRow}>
                                <label htmlFor="password" className={styles.label}>Password</label>
                                <Link to="/forgot-password" className={styles.forgotLink}>Forgot password?</Link>
                            </div>
                            <div className={styles.inputWrapper}>
                                <Lock size={17} className={styles.inputIcon} />
                                <input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className={styles.input}
                                    required
                                    disabled={isLoading}
                                    autoComplete="current-password"
                                />
                                <button
                                    type="button"
                                    className={styles.eyeBtn}
                                    onClick={() => setShowPassword(v => !v)}
                                    tabIndex={-1}
                                    aria-label="Toggle password visibility"
                                >
                                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                        </div>

                        <div className={styles.rememberRow}>
                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                    className={styles.checkbox}
                                />
                                <span className={styles.checkmark} />
                                Remember me for 30 days
                            </label>
                        </div>

                        <Button
                            type="submit"
                            className={`${styles.submitBtn} btn-pill-primary`}
                            disabled={isLoading}
                            loading={isLoading}
                            iconRight={!isLoading ? <ArrowRight size={17} /> : null}
                        >
                            {isLoading ? 'Signing in...' : 'Sign in'}
                        </Button>
                    </form>

                    <p className={styles.bottomLink}>
                        New to LivePulse? <Link to="/signup">Create an account</Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Login;
